import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  StatusBar,
  RefreshControl,
  StyleSheet,
  ViewStyle,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useResponsive } from '@/hooks/common/useResponsive';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { SellerProfile, MarketplaceListing } from '@/types/residents/marketplaceListing';
import { useListingNavigation } from '@/hooks/residents/useListingNavigation';
import { useGetSellerProfileQuery, useToggleFollowMutation } from '@/store/api';
import { useAppSelector } from '@/store';
import { selectUser } from '@/store/selectors/userSelectors';
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { BackLink } from '@/components/residents/mobile/BackLink';
import {
  GradientBackground,
  GlassCard,
  PageHeader,
  SectionHeader,
} from '@/components/residents/redesign';
import { MarketplaceListingCard } from '@/components/residents/redesign/marketplace';

type SellerProfileNavigationProp = StackNavigationProp<RootStackParamList, 'SellerProfile'>;
type SellerProfileRouteProp = RouteProp<RootStackParamList, 'SellerProfile'>;

const CheckIcon = RESIDENTS_SVG_COMPONENTS.CHECK;
const StarFilledIcon = RESIDENTS_SVG_COMPONENTS.MARKETPLACE_STAR;
const StarGrayIcon = RESIDENTS_SVG_COMPONENTS.STAR_GRAY;

const HP = REDESIGN_SPACING.horizontalPadding;
const MAX_CONTENT_WIDTH = 1200;

const DEFAULT_PROFILE: SellerProfile = {
  id: '',
  name: '',
  initials: '',
  rating: 0,
  reviewCount: 0,
  bought: 0,
  sold: 0,
  followers: 0,
  following: 0,
  isFollowing: false,
  listings: [],
};

// =============================================================================
// Main Screen — responsive split
// =============================================================================

export const SellerProfileScreen: React.FC = () => {
  const { layoutMode } = useResponsive();
  if (layoutMode === 'desktop') return <DesktopSellerProfileView />;
  return <MobileSellerProfileView />;
};

// =============================================================================
// Shared hook
// =============================================================================

function useSellerProfileData() {
  const navigation = useNavigation<SellerProfileNavigationProp>();
  const route = useRoute<SellerProfileRouteProp>();
  const { isWeb } = useResponsive();
  const { sellerId } = route.params;
  const { navigateToListing } = useListingNavigation();

  const currentUser = useAppSelector(selectUser);
  const isOwnProfile =
    currentUser?.accountId?.toString() === sellerId ||
    currentUser?.id?.toString() === sellerId;

  const {
    data,
    isLoading,
    refetch,
  } = useGetSellerProfileQuery(sellerId);

  const sellerProfile = data ?? DEFAULT_PROFILE;

  const [isFollowing, setIsFollowing] = useState(sellerProfile.isFollowing);
  const [toggleFollow] = useToggleFollowMutation();

  useEffect(() => {
    setIsFollowing(sellerProfile.isFollowing);
  }, [sellerProfile.isFollowing]);

  const handleFollowPress = useCallback(async () => {
    const newState = !isFollowing;
    setIsFollowing(newState);
    try {
      await toggleFollow(sellerId).unwrap();
    } catch (error) {
      setIsFollowing(!newState);
      console.error('Failed to toggle follow:', error);
    }
  }, [isFollowing, sellerId, toggleFollow]);

  const handleListingPress = useCallback(
    (listing: MarketplaceListing) => navigateToListing(listing, true),
    [navigateToListing],
  );

  const handleBack = useCallback(() => navigation.goBack(), [navigation]);

  const handleGoToMarketplace = useCallback(() => {
    if (isWeb) {
      navigation.navigate('Marketplace' as any);
    } else {
      navigation.navigate('Main', { screen: 'ResidentMarketplace' } as any);
    }
  }, [isWeb, navigation]);

  const firstName = sellerProfile.name ? sellerProfile.name.split(' ')[0] : '';

  return {
    sellerProfile,
    isLoading,
    isOwnProfile,
    isFollowing,
    firstName,
    refetch,
    handleFollowPress,
    handleListingPress,
    handleBack,
    handleGoToMarketplace,
  };
}

// =============================================================================
// Mobile View
// =============================================================================

const MobileSellerProfileView: React.FC = () => {
  const { colors } = useThemeColors();
  const insets = useSafeAreaInsets();
  const {
    sellerProfile,
    isLoading,
    isOwnProfile,
    isFollowing,
    firstName,
    refetch,
    handleFollowPress,
    handleListingPress,
    handleBack,
  } = useSellerProfileData();

  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  if (isLoading) {
    return (
      <GradientBackground>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={PRIMITIVE_COLORS.resioBlue} />
        </View>
      </GradientBackground>
    );
  }

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />
      <BaseResidentMobileScreen
        screenName="Marketplace"
        darkBackground={true}
        headerVariant="gradient"
        contentContainerStyle={{ paddingBottom: insets.bottom }}
      >
        <ScrollView
          style={styles.mobileScroll}
          contentContainerStyle={styles.mobileScrollContent}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={colors.text.primary}
            />
          }
        >
          <BackLink
            label="Back to marketplace"
            onPress={handleBack}
            color={PRIMITIVE_COLORS.resioBlue}
          />

          <PageHeader title="Seller Profile" />

          <SellerProfileCard
            profile={sellerProfile}
            isOwnProfile={isOwnProfile}
            isFollowing={isFollowing}
            onFollowPress={handleFollowPress}
          />

          <SectionHeader title="Statistics" />
          <SellerStatsCard profile={sellerProfile} />

          <SellerListingsSection
            listings={sellerProfile.listings}
            firstName={firstName}
            isOwnProfile={isOwnProfile}
            onListingPress={handleListingPress}
          />
        </ScrollView>
      </BaseResidentMobileScreen>
    </GradientBackground>
  );
};

// =============================================================================
// Desktop View
// =============================================================================

const DesktopSellerProfileView: React.FC = () => {
  const { colors } = useThemeColors();
  const {
    sellerProfile,
    isLoading,
    isOwnProfile,
    isFollowing,
    firstName,
    refetch,
    handleFollowPress,
    handleListingPress,
    handleBack,
  } = useSellerProfileData();

  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={PRIMITIVE_COLORS.resioBlue} />
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.desktopContainer}
      contentContainerStyle={styles.desktopScrollContent}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={handleRefresh}
          tintColor={colors.text.primary}
        />
      }
    >
      <View style={styles.desktopCentered}>
        <BackLink
          label="Back to marketplace"
          onPress={handleBack}
          color={PRIMITIVE_COLORS.resioBlue}
        />

        <PageHeader title="Seller Profile" />

        <SellerProfileCard
          profile={sellerProfile}
          isOwnProfile={isOwnProfile}
          isFollowing={isFollowing}
          onFollowPress={handleFollowPress}
        />

        <SectionHeader title="Statistics" />
        <SellerStatsCard profile={sellerProfile} />

        <SellerListingsSection
          listings={sellerProfile.listings}
          firstName={firstName}
          isOwnProfile={isOwnProfile}
          onListingPress={handleListingPress}
          numColumns={3}
        />
      </View>
    </ScrollView>
  );
};

// =============================================================================
// SellerProfileCard — avatar, name, rating, follow button
// =============================================================================

const SellerProfileCard: React.FC<{
  profile: SellerProfile;
  isOwnProfile: boolean;
  isFollowing: boolean;
  onFollowPress: () => void;
}> = memo(({ profile, isOwnProfile, isFollowing, onFollowPress }) => {
  const { colors } = useThemeColors();
  return (
    <GlassCard borderRadius={20} padding={20}>
      <View style={styles.profileTop}>
        <View style={[styles.avatar, { backgroundColor: colors.accent.blueBgMedium }]}>
          <Text style={[styles.avatarText, { color: colors.text.primary }]}>{profile.initials}</Text>
        </View>
        <View style={styles.profileInfo}>
          <Text style={[styles.sellerName, { color: colors.text.primary }]}>{profile.name}</Text>
          <View style={styles.ratingRow}>
            <StarRatingRow rating={profile.rating} />
            <Text style={[styles.reviewCount, { color: colors.text.muted }]}>({profile.reviewCount})</Text>
          </View>
        </View>
      </View>

      {!isOwnProfile && (
        <TouchableOpacity
          style={[styles.followButton, isFollowing && styles.followingButton]}
          onPress={onFollowPress}
          activeOpacity={0.8}
        >
          {isFollowing && (
            <CheckIcon width={16} height={16} color={PRIMITIVE_COLORS.resioBlue} />
          )}
          <Text style={[styles.followButtonText, { color: colors.text.primary }, isFollowing && styles.followingButtonText]}>
            {isFollowing ? 'Following' : 'Follow'}
          </Text>
        </TouchableOpacity>
      )}
    </GlassCard>
  );
});

SellerProfileCard.displayName = 'SellerProfileCard';

// =============================================================================
// SellerStatsCard — bought, sold, followers, following
// =============================================================================

const SellerStatsCard: React.FC<{ profile: SellerProfile }> = memo(({ profile }) => (
  <GlassCard borderRadius={16} padding={16}>
    <View style={styles.statsRow}>
      <StatValue value={profile.bought} label="Bought" />
      <StatValue value={profile.sold} label="Sold" />
      <StatValue value={profile.followers} label="Followers" />
      <StatValue value={profile.following} label="Following" />
    </View>
  </GlassCard>
));

SellerStatsCard.displayName = 'SellerStatsCard';

const StatValue: React.FC<{ value: number; label: string }> = memo(({ value, label }) => {
  const { colors } = useThemeColors();
  return (
    <View style={styles.statItem}>
      <Text style={[styles.statItemValue, { color: colors.text.primary }]}>{value}</Text>
      <Text style={[styles.statItemLabel, { color: colors.text.muted }]}>{label}</Text>
    </View>
  );
});

StatValue.displayName = 'StatValue';

// =============================================================================
// SellerListingsSection — grid of listings
// =============================================================================

const CARD_HEIGHT_MOBILE = 246;
const CARD_HEIGHT_DESKTOP = 300;

const SellerListingsSection: React.FC<{
  listings: MarketplaceListing[];
  firstName: string;
  isOwnProfile: boolean;
  onListingPress: (listing: MarketplaceListing) => void;
  numColumns?: number;
}> = memo(({ listings, firstName, isOwnProfile, onListingPress, numColumns = 2 }) => {
  const { colors } = useThemeColors();
  const title = isOwnProfile ? 'Your listings' : `${firstName}'s listings`;

  const dynamicItemStyle = useMemo<ViewStyle>(() => {
    const basis = `${Math.floor(100 / numColumns) - 2}%`;
    return {
      flexBasis: basis as any,
      flexGrow: 0,
      flexShrink: 0,
      height: numColumns >= 3 ? CARD_HEIGHT_DESKTOP : CARD_HEIGHT_MOBILE,
    };
  }, [numColumns]);

  return (
    <View>
      <SectionHeader title={title} />

      {listings.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Text style={[styles.emptyTitle, { color: colors.text.secondary }]}>No listings yet</Text>
          <Text style={[styles.emptySubtitle, { color: colors.text.dim }]}>
            {isOwnProfile
              ? 'Post an item to get started'
              : `${firstName} hasn't posted any items yet`}
          </Text>
        </View>
      ) : (
        <View style={styles.listingsGrid}>
          {listings.map((listing) => (
            <MarketplaceListingCard
              key={listing.id}
              listing={listing}
              onPress={() => onListingPress(listing)}
              style={dynamicItemStyle}
            />
          ))}
        </View>
      )}
    </View>
  );
});

SellerListingsSection.displayName = 'SellerListingsSection';

// =============================================================================
// StarRatingRow
// =============================================================================

const StarRatingRow: React.FC<{ rating: number }> = memo(({ rating }) => {
  const filled = Math.floor(rating);
  const stars = [];
  for (let i = 0; i < 5; i++) {
    const Icon = i < filled ? StarFilledIcon : StarGrayIcon;
    stars.push(<Icon key={i} width={14} height={14} />);
  }
  return <View style={styles.starRow}>{stars}</View>;
});

StarRatingRow.displayName = 'StarRatingRow';

// =============================================================================
// Styles
// =============================================================================

const styles = StyleSheet.create({
  // Layout -- Mobile
  mobileScroll: {
    flex: 1,
  },
  mobileScrollContent: {
    paddingHorizontal: HP,
    paddingBottom: 40,
    gap: 8,
  },

  // Layout -- Desktop
  desktopContainer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  desktopScrollContent: {
    paddingHorizontal: HP,
    paddingTop: 16,
    paddingBottom: 60,
    alignItems: 'center',
  },
  desktopCentered: {
    width: '100%',
    maxWidth: MAX_CONTENT_WIDTH,
    gap: 8,
  },

  // Loading
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },

  // Profile Card
  profileTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  avatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    ...REDESIGN_TYPOGRAPHY.heading1,
    fontSize: 22,
    textTransform: 'uppercase',
  },
  profileInfo: {
    flex: 1,
    gap: 6,
  },
  sellerName: {
    ...REDESIGN_TYPOGRAPHY.sectionTitle,
  },
  ratingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  starRow: {
    flexDirection: 'row',
    gap: 2,
  },
  reviewCount: {
    ...REDESIGN_TYPOGRAPHY.small,
  },

  // Follow Button
  followButton: {
    height: 40,
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginTop: 16,
  },
  followingButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: PRIMITIVE_COLORS.resioBlue,
  },
  followButtonText: {
    ...REDESIGN_TYPOGRAPHY.tagFilter,
  },
  followingButtonText: {
    color: PRIMITIVE_COLORS.resioBlue,
  },

  // Stats
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
    minWidth: 52,
  },
  statItemValue: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
  },
  statItemLabel: {
    ...REDESIGN_TYPOGRAPHY.body,
  },

  // Listings Grid
  listingsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: REDESIGN_SPACING.gridGap,
  },

  // Empty State
  emptyContainer: {
    paddingVertical: 40,
    alignItems: 'center',
    gap: 4,
  },
  emptyTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
  },
  emptySubtitle: {
    ...REDESIGN_TYPOGRAPHY.body,
    textAlign: 'center',
  },
});
